export type PricingModel =
  | { kind: 'free' }
  | {
      kind: 'per-child';
      /** Monthly minimum (covers up to {@link minimumChildren} children). */
      startingMonthly: number;
      /** Per-month price under annual billing (10% off). */
      startingAnnual: number;
      /** Per-child rate beyond the minimum, billed monthly. */
      perChildMonthly: number;
      /** Per-child rate under annual billing. */
      perChildAnnual: number;
      /** Children covered by the minimum (e.g. 25 → R299 / 25 = R12 each). */
      minimumChildren: number;
    };

export interface PricingTier {
  name: string;
  tier: 'seedling' | 'sprout';
  pricing: PricingModel;
  childrenRange: string;
  description: string;
  features: string[];
  /** Things NOT in this tier, shown as a strikethrough list (Seedling uses this). */
  notIncluded?: string[];
  cta: string;
}

export const pricingTiers: PricingTier[] = [
  {
    name: 'Seedling',
    tier: 'seedling',
    pricing: { kind: 'free' },
    childrenRange: 'Unlimited children',
    description: 'Free forever for text-based observations. Perfect for schools starting their digital journey.',
    features: [
      'Unlimited children',
      'Unlimited teacher accounts',
      'Text observations & assessments',
      'CAPS curriculum tracking',
      'Parent portal (text updates)',
      'POPIA compliance',
      'Community support',
    ],
    notIncluded: [
      'Photos & video uploads',
      'PDF reports',
      'Advanced analytics',
    ],
    cta: 'Start Free Forever',
  },
  {
    name: 'Sprout',
    tier: 'sprout',
    pricing: {
      kind: 'per-child',
      startingMonthly: 299,
      startingAnnual: 269,
      perChildMonthly: 12,
      perChildAnnual: 11,
      minimumChildren: 25,
    },
    childrenRange: 'For schools ready to grow',
    description: 'Everything you need to run digital observations end-to-end — media, reports, analytics, audit logs, and team controls. Pricing scales with the number of children on your roll.',
    features: [
      'Everything in Seedling',
      'Photos & video uploads',
      'PDF report exports',
      'Multiple classrooms',
      'Analytics dashboard',
      'Bulk operations',
      'Role-based access control',
      'Audit logs & data export',
      'Priority email support',
    ],
    cta: 'Start 14-Day Trial',
  },
];

export const pricingFAQ = [
  {
    question: 'Is the free plan really unlimited?',
    answer: 'Yes. Seedling is free forever with unlimited children and teachers. The only limit is that photos and videos are not included — text observations, assessments, CAPS tracking, and the parent portal are all fully available on the free plan.',
  },
  {
    question: 'How does Sprout per-child pricing work?',
    answer: 'Sprout starts at R299 per month, which covers your first 25 children. Each additional child is R12/month (R11 if billed annually). So a 30-child centre pays R299 + 5 × R12 = R359/month. We bill monthly based on the number of active children on your roll at the start of the cycle.',
  },
  {
    question: 'Why per-child pricing?',
    answer: "Each child's record adds observations, assessments, and (on Sprout) photos to our hosting bill, so per-child pricing keeps things sustainable for us and fair for you — small centres pay small bills, larger schools pay more proportionally. It's also how every other ECD platform in this market works, so the model will feel familiar.",
  },
  {
    question: 'What do I give up on the free plan?',
    answer: 'Photos, videos, PDF reports, and advanced analytics are reserved for the paid plan. You can always upgrade when you need those features. Children and teacher accounts stay unlimited on the free plan.',
  },
  {
    question: 'How does the 14-day trial work?',
    answer: "After signing up for the free Seedling plan, you can start a 14-day trial of Sprout from the pricing page. No credit card needed. When the trial ends, you go back to Seedling — your data stays. Each organisation gets one trial period.",
  },
  {
    question: 'Do you offer discounts for NPOs?',
    answer: "Get in touch — we're happy to talk about pricing for registered non-profit ECD centres.",
  },
  {
    question: 'Is my data safe?',
    answer: 'Your data is encrypted at rest and in transit, hosted on managed cloud infrastructure with cross-border safeguards under POPIA, and we run with audit logs and consent records on every plan, including free. See /docs/security/data-residency for the full picture.',
  },
  {
    question: 'Can I export my data?',
    answer: 'Yes, all plans include the ability to export your data. We believe your data belongs to you.',
  },
];
